import { defineStore } from 'pinia';
import type { RouteParamsRaw } from 'vue-router';
import { useRoute, useRouter } from 'vue-router';
import { ref } from 'vue';
import { watchOnce } from '@vueuse/core';

import type { Landing } from '@libs/trip-planner-interfaces';
import { assertValue } from '@libs/utils';

import { TripPlannerPage } from '../models/navigation';

import { useRecipeStore } from './recipe/recipeStore';

type NavigationIndex = { stayOnlyIndex: number } | { ingredientIndex: number };

export const useRouterStore = defineStore('RouterStore', () => {
  const router = useRouter();
  const route = useRoute();
  const recipeStore = useRecipeStore();

  // State
  const isNavigating = ref(false);
  const landingRoute = ref<TripPlannerPage>();
  const isInitialLoad = ref<boolean>(true);
  const landing = ref<Landing | undefined>(undefined);
  const previousTripPlannerPage = ref<TripPlannerPage | undefined>(undefined);

  // Getters - functions
  function getCurrentTripPlannerView(): TripPlannerPage | undefined {
    return Object.values(TripPlannerPage).find(
      (tripPlannerPage) => tripPlannerPage === route.name
    );
  }

  function getTripOverviewUrl(): string {
    const queryParams = window.location.search;
    return `/travel/plan/overview${queryParams}`;
  }

  function getHotelSelectionUrl(navigationIndex: NavigationIndex): string {
    const queryParams = window.location.search;
    const stayOnlyIndex = getStayOnlyIndex(navigationIndex);

    assertValue(
      stayOnlyIndex,
      'routerStore > getHotelSelectionUrl > stayOnlyIndex'
    );

    return `/travel/plan/hotel-selection/h${stayOnlyIndex}${queryParams}`;
  }

  function getHotelDetailsUrlInHotelSelection(
    navigationIndex: NavigationIndex,
    hotelCode: string
  ): string {
    const queryParams = window.location.search;
    const stayOnlyIndex = getStayOnlyIndex(navigationIndex);

    assertValue(
      stayOnlyIndex,
      'routerStore > getHotelDetailsUrlInHotelSelection > stayOnlyIndex'
    );
    return `/travel/plan/hotel-selection/h${stayOnlyIndex}/hotel-details/${hotelCode}${queryParams}`;
  }

  function getFlightListUrl(): string {
    const queryParams = window.location.search;
    return `/travel/plan/flights${queryParams}`;
  }

  function getFerryListUrl(): string {
    const queryParams = window.location.search;
    return `/travel/plan/ferry${queryParams}`;
  }

  function getActivitySelectionUrl(navigationIndex: NavigationIndex): string {
    const queryParams = window.location.search;
    const stayOnlyIndex = getStayOnlyIndex(navigationIndex);

    return `/travel/plan/activity-selection/h${stayOnlyIndex}${queryParams}`;
  }

  function getActivityDetailsUrlInActivitySelection(
    navigationIndex: NavigationIndex,
    activityCode: string
  ): string {
    const queryParams = window.location.search;
    const stayOnlyIndex = getStayOnlyIndex(navigationIndex);

    return `/travel/plan/activity-selection/h${stayOnlyIndex}/activity-details/${activityCode}${queryParams}`;
  }

  // Actions
  function setLanding(param: Landing): void {
    landing.value = param;
  }

  function setLandingRoute(): void {
    landingRoute.value = getCurrentTripPlannerView();
  }

  async function navigateToTripOverview(): Promise<void> {
    await navigate({ name: TripPlannerPage.TripOverview });
  }

  async function navigateToCarTrip(): Promise<void> {
    await navigate({ name: TripPlannerPage.CarTrip });
  }

  async function navigateToFlightsList(): Promise<void> {
    await navigate({ name: TripPlannerPage.FlightList });
  }

  async function navigateToFerryList(): Promise<void> {
    await navigate({ name: TripPlannerPage.FerryList });
  }

  async function navigateToHotelSelection(
    navigationIndex: NavigationIndex
  ): Promise<void> {
    const stayOnlyIndex = getStayOnlyIndex(navigationIndex);

    assertValue(
      stayOnlyIndex,
      'routerStore > navigateToHotelSelection > stayOnlyIndex'
    );

    await navigate({
      name: TripPlannerPage.HotelSelection,
      params: { ingredientIndex: `h${stayOnlyIndex}` },
    });
  }

  async function navigateToActivitySelection(
    navigationIndex: NavigationIndex
  ): Promise<void> {
    const stayOnlyIndex = getStayOnlyIndex(navigationIndex);

    assertValue(
      stayOnlyIndex,
      'routerStore > navigateToActivitySelection > stayOnlyIndex'
    );

    await navigate({
      name: TripPlannerPage.ActivitySelection,
      params: { ingredientIndex: `h${stayOnlyIndex}` },
    });
  }

  async function navigateToTripOverviewPrint(): Promise<void> {
    await navigate({
      name: TripPlannerPage.TripOverviewPrint,
    });
  }

  async function navigateToHotelDetailsInHotelSelection(
    navigationIndex: NavigationIndex,
    hotelCode: string
  ): Promise<void> {
    const stayOnlyIndex = getStayOnlyIndex(navigationIndex);

    assertValue(
      stayOnlyIndex,
      'routerStore > navigateToHotelDetailsInHotelSelection > stayOnlyIndex'
    );

    await navigate({
      name: TripPlannerPage.HotelDetailsInHotelSelection,
      params: { ingredientIndex: `h${stayOnlyIndex}`, hotelCode },
    });
  }

  async function navigateToActivityDetailsInActivitySelection(
    navigationIndex: NavigationIndex,
    activityCode: string
  ): Promise<void> {
    const stayOnlyIndex = getStayOnlyIndex(navigationIndex);

    assertValue(
      stayOnlyIndex,
      'routerStore > navigateToActivityDetailsInActivitySelection > stayOnlyIndex'
    );

    await navigate({
      name: TripPlannerPage.ActivityDetailsInActivitySelection,
      params: { ingredientIndex: `h${stayOnlyIndex}`, activityCode },
    });
  }

  async function navigate({
    name,
    params,
  }: {
    name: TripPlannerPage;
    params?: RouteParamsRaw;
  }): Promise<void> {
    await afterNavigation();
    await router.push({
      name,
      params,
      query: route.query,
    });
  }

  async function afterNavigation(): Promise<void> {
    return new Promise((resolve) => {
      if (!isNavigating.value) {
        resolve();
      } else {
        watchOnce(isNavigating, async () => resolve());
      }
    });
  }

  function getStayOnlyIndex(
    navigationIndex: NavigationIndex
  ): number | undefined {
    return 'stayOnlyIndex' in navigationIndex
      ? navigationIndex.stayOnlyIndex
      : recipeStore.mapRecipeIndexToStayOnlyIndex(
          navigationIndex.ingredientIndex
        );
  }

  return {
    // State
    isInitialLoad,
    isNavigating,
    landing,
    landingRoute,
    previousTripPlannerPage,

    // Getters - functions
    getCurrentTripPlannerView,
    getTripOverviewUrl,
    getHotelSelectionUrl,
    getHotelDetailsUrlInHotelSelection,
    getActivityDetailsUrlInActivitySelection,
    getFlightListUrl,
    getFerryListUrl,
    getActivitySelectionUrl,

    // Actions
    navigateToActivitySelection,
    navigateToFerryList,
    navigateToFlightsList,
    navigateToHotelSelection,
    navigateToTripOverview,
    navigateToCarTrip,
    navigateToTripOverviewPrint,
    navigateToHotelDetailsInHotelSelection,
    navigateToActivityDetailsInActivitySelection,
    setLanding,
    setLandingRoute,
  };
});
